import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEyeSlash,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";

import {
  validateActivationToken,
  createProviderPassword,
} from "../../services/providerService";

import "./CreatePassword.css";

export default function CreatePassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");

  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const [showPassword, setShowPassword] =
    useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) {
      setMessage("Activation token is missing.");
      setLoading(false);
      return;
    }

    verifyToken();
  }, []);

  const verifyToken = async () => {
    try {
      await validateActivationToken(token);
      setValid(true);
    } catch (err) {
      setMessage(
        err.response?.data?.message ||
          "Invalid or expired activation link."
      );
    } finally {
      setLoading(false);
    }
  };

  // Password Strength
  const getStrength = () => {
    let score = 0;

    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    return score;
  };

  const strength = getStrength();

  const strengthText = [
    "Very Weak",
    "Weak",
    "Fair",
    "Good",
    "Strong",
    "Excellent",
  ][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");

    if (password.length < 8) {
      setMessage(
        "Password must contain at least 8 characters."
      );
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    try {
      setCreating(true);

      const res = await createProviderPassword({
        token,
        password,
        confirmPassword,
      });

      setSuccess(true);

      setMessage(
        res.message ||
          "Password created successfully!"
      );

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setMessage(
        err.response?.data?.message ||
          "Failed to create password."
      );
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="create-password-page">
        <div className="password-card">
          <div className="loader"></div>

          <h2>Verifying Link...</h2>

          <p>
            Please wait while we verify your
            activation link.
          </p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="create-password-page">
        <div className="password-card">

          <div className="failed-icon">❌</div>

          <h2>Activation Failed</h2>

          <p className="error-message">
            {message}
          </p>

          <button
            className="primary-btn"
            onClick={() => navigate("/")}
          >
            Back to Home
          </button>

        </div>
      </div>
    );
  }

  return (
    <div className="create-password-page">

      <div className="password-card">

        <div className="logo-circle">
          <FaLock />
        </div>

        <h1>Create Password</h1>

        <p className="subtitle">
          Your provider account has been
          approved.
          <br />
          Create a secure password to continue.
        </p>

        <form onSubmit={handleSubmit}>

          {/* Password */}

          <div className="input-group">

            <input
              type={
                showPassword ? "text" : "password"
              }
              placeholder="New Password"
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              required
            />

            <span
              className="eye"
              onClick={() =>
                setShowPassword(!showPassword)
              }
            >
              {showPassword ? (
                <FaEyeSlash />
              ) : (
                <FaEye />
              )}
            </span>

          </div>

          {/* Strength */}

          <div className="strength-bar">

            <div
              className={`strength-fill strength-${strength}`}
            ></div>

          </div>

          <small className="strength-text">
            {strengthText}
          </small>

          {/* Confirm */}

          <div className="input-group">

            <input
              type={
                showConfirmPassword
                  ? "text"
                  : "password"
              }
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) =>
                setConfirmPassword(
                  e.target.value
                )
              }
              required
            />

            <span
              className="eye"
              onClick={() =>
                setShowConfirmPassword(
                  !showConfirmPassword
                )
              }
            >
              {showConfirmPassword ? (
                <FaEyeSlash />
              ) : (
                <FaEye />
              )}
            </span>

          </div>

          {confirmPassword.length > 0 && (
            <div
              className={
                password === confirmPassword
                  ? "match success"
                  : "match error"
              }
            >
              {password === confirmPassword
                ? "✔ Passwords Match"
                : "✖ Passwords Do Not Match"}
            </div>
          )}

          <div className="password-rules">

            <div
              className={
                password.length >= 8
                  ? "rule valid"
                  : "rule"
              }
            >
              <FaCheckCircle />
              Minimum 8 Characters
            </div>

            <div
              className={
                /[A-Z]/.test(password)
                  ? "rule valid"
                  : "rule"
              }
            >
              <FaCheckCircle />
              One Uppercase Letter
            </div>

            <div
              className={
                /[0-9]/.test(password)
                  ? "rule valid"
                  : "rule"
              }
            >
              <FaCheckCircle />
              One Number
            </div>

          </div>

          <button
            className="primary-btn"
            disabled={creating}
          >
            {creating
              ? "Creating..."
              : "Create Password"}
          </button>

        </form>

        {message && (
          <div
            className={
              success
                ? "success-message"
                : "error-message"
            }
          >
            {message}
          </div>
        )}

      </div>

    </div>
  );
}